import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MaskedLines } from "@/components/Reveal";
import { TEAM } from "@/data/site";
import { Sparkles } from "lucide-react";

// Vibrant color palette cycles for individual avatar borders & glows
const AVATAR_COLORS = [
  { border: "border-cyan-400", shadow: "hover:shadow-cyan-400/50", text: "text-cyan-400" },
  { border: "border-rose-400", shadow: "hover:shadow-rose-400/50", text: "text-rose-400" },
  { border: "border-amber-400", shadow: "hover:shadow-amber-400/50", text: "text-amber-400" },
  { border: "border-emerald-400", shadow: "hover:shadow-emerald-400/50", text: "text-emerald-400" },
  { border: "border-violet-400", shadow: "hover:shadow-violet-400/50", text: "text-violet-400" },
  { border: "border-sky-400", shadow: "hover:shadow-sky-400/50", text: "text-sky-400" },
  { border: "border-fuchsia-400", shadow: "hover:shadow-fuchsia-400/50", text: "text-fuchsia-400" },
];

export default function TeamHeader() {
  return (
    <header
      data-testid="team-header"
      className="relative w-full overflow-hidden bg-slate-950 text-white"
    >
      {/* ================= BACKGROUND COLORFUL MESH ================= */}
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.35, 0.6, 0.35],
          rotate: [0, 90, 0],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-20 -right-20 w-[32rem] h-[32rem] rounded-full bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 blur-3xl pointer-events-none opacity-40"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.55, 0.3],
          rotate: [0, -90, 0],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-32 -left-20 w-[32rem] h-[32rem] rounded-full bg-gradient-to-tr from-teal-500 via-cyan-500 to-blue-600 blur-3xl pointer-events-none opacity-40"
      />
      <motion.div
        animate={{
          scale: [0.9, 1.2, 0.9],
          opacity: [0.25, 0.45, 0.25],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/3 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 blur-3xl pointer-events-none opacity-30"
      />

      {/* ================= CONTENT CONTAINER ================= */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-20 md:pb-24 z-10">
        
        {/* Top Tagline Pill */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-violet-500/20 via-fuchsia-500/20 to-rose-500/20 backdrop-blur-md border border-white/20 mb-6 shadow-lg shadow-violet-500/10"
        >
          <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] bg-gradient-to-r from-teal-200 via-amber-200 to-pink-200 bg-clip-text text-transparent font-bold">
            Our Leadership & Collective
          </span>
        </motion.div>

        {/* Hero Title */}
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
          <MaskedLines lines={["The people behind", "the mission."]} delay={0.15} />
        </h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="mt-6 max-w-2xl text-base sm:text-lg lg:text-xl leading-relaxed text-slate-200"
        >
          Educators, clinicians, developers, and young leaders united for{" "}
          <h1><span className="bg-gradient-to-r from-teal-300 via-sky-300 to-amber-300 bg-clip-text text-transparent font-bold">
          Youth Health Literacy
          </span>
          .</h1>
          <span className="block text-sm text-amber-300/90 font-mono mt-3 font-semibold">
            ✨ Hover a face to reveal their role, or click to view their full profile.
          </span>
        </motion.p>

        {/* Dynamic Overlapping Rainbow Avatar Row */}
        <div className="mt-12 flex flex-wrap items-center gap-3 sm:gap-4 pt-4">
          {TEAM.map((m, i) => {
            const theme = AVATAR_COLORS[i % AVATAR_COLORS.length];

            return (
              <motion.div
                key={m.slug}
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  delay: 0.4 + i * 0.04,
                  duration: 0.5,
                  ease: [0.76, 0, 0.24, 1],
                }}
                whileHover={{ y: -8, scale: 1.15, zIndex: 30 }}
                className="relative group cursor-pointer"
              >
                {/* Colorful Tooltip Bubble */}
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-20 z-40 opacity-0 group-hover:opacity-100 group-hover:-translate-y-2 transition-all duration-300 ease-out">
                  <div className="relative whitespace-nowrap rounded-2xl bg-slate-900/95 text-white p-3 shadow-2xl border border-white/20 backdrop-blur-md flex flex-col items-center">
                    <span className="font-heading font-bold text-sm leading-snug">
                      {m.name}
                    </span>
                    <span className={`font-mono text-[10px] uppercase tracking-wider font-extrabold mt-0.5 ${theme.text}`}>
                      {m.role}
                    </span>
                    {/* Subtle triangle point */}
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 rotate-45 bg-slate-900 border-r border-b border-white/20" />
                  </div>
                </div>

                {/* Avatar Link with Rainbow Ring & Dynamic Glow */}
                <Link
                  to={`/team/${m.slug}`}
                  data-testid={`team-face-${m.slug}`}
                  aria-label={m.name}
                  className={`block relative w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 ${theme.border} shadow-lg ${theme.shadow} transition-all duration-300`}
                >
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-all duration-500"
                  />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </header>
  );
}